import { useContext, useEffect, useState } from "react";
import { MdArrowBack, MdCheckCircle, MdOutlineTravelExplore } from "react-icons/md";
import { AppContext } from "../App";
import { CDN_URL, getInlineLoader } from "../Helpers";
import Footer from "../ones/Footer";

function getServiceImage(photo = "") {
  if (!photo) {
    return "";
  }

  if (String(photo).startsWith("http://") || String(photo).startsWith("https://")) {
    return photo;
  }

  return `${CDN_URL}${photo}`;
}

function stripHtml(value = "") {
  return String(value).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function extractHighlights(body = "") {
  const matches = String(body).match(/<strong[^>]*>.*?<\/strong>/gi) || [];
  const uniqueItems = [];

  matches.forEach((item) => {
    const cleanItem = stripHtml(item);

    if (cleanItem && !uniqueItems.includes(cleanItem)) {
      uniqueItems.push(cleanItem);
    }
  });

  return uniqueItems;
}

export default function ServiceDetailsLanding({ uuid }) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [service, setService] = useState(null);

  async function init() {
    setReady(false);

    if (!appContext.allServices) {
      await appContext.getAllServices?.();
    }

    setReady(true);
  }

  useEffect(() => {
    init();
  }, [uuid]);

  useEffect(() => {
    if (appContext.allServices && uuid) {
      const foundService = appContext.allServices.find((item) => item.uuid === uuid) || null;
      setService(foundService);
    }
  }, [appContext.allServices, uuid]);

  if (!ready) {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }

  if (!service) {
    return (
      <>
        <div className="MainBodySection bg-background service-detail-empty">
          <div className="container text-center">
            <h2 className="tff-section-title">Service Not Found</h2>
            <p className="font-light tff-section-lead">
              The selected service could not be found in the current published feed.
            </p>
            <button
              className="btn btn-primary btn-rounded btn-padded mt-3"
              onClick={() => {
                appContext.navTo({ item: "services" });
              }}
            >
              Back to Services
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const highlights = extractHighlights(service.body);

  return (
    <>
      <div className="MainBodySection bg-background service-detail-page">
        <div className="container">
          <button
            className="btn btn-outline-primary btn-rounded btn-padded mb-4"
            onClick={() => {
              appContext.navTo({ item: "services" });
            }}
          >
            <MdArrowBack size={18} className="me-2" />
            Back to Services
          </button>

          <div className="card service-detail-hero-card overflow-hidden">
            <div className="row g-0">
              <div className="col-lg-6">
                <div
                  className="service-detail-hero-image"
                  style={{
                    backgroundImage: `url(${getServiceImage(service.photo)})`,
                  }}
                />
              </div>

              <div className="col-lg-6">
                <div className="card-body p-4 p-lg-5">
                  <div className="tff-section-kicker">
                    {service.category_title || "Tourism Service"}
                  </div>
                  <h1 className="tff-section-title service-detail-title">
                    {service.title}
                    <span className="tff-section-underline"></span>
                  </h1>
                  <p className="font-light tff-section-lead mb-4">
                    {service.description}
                  </p>

                  {highlights.length > 0 ? (
                    <div className="service-detail-highlights">
                      {highlights.map((item) => (
                        <div key={item} className="tff-inline-list-item">
                          <MdCheckCircle size={20} className="text-secondary me-2 flex-shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    ""
                  )}

                  <button
                    className="btn btn-primary btn-rounded btn-padded mt-4"
                    onClick={() => {
                      appContext.navTo({ item: "contact_us" });
                    }}
                  >
                    Inquire About This Service
                    <MdOutlineTravelExplore size={18} className="ms-2" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card service-detail-body-card">
            <div className="card-body p-4 p-lg-5">
              <div className="tff-section-kicker">Service Overview</div>
              <div
                className="service-detail-body mt-4"
                dangerouslySetInnerHTML={{ __html: service.body || "" }}
              />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
